import React, { useCallback } from "react";
import { Empty, Spin } from "antd";
import {
  ArrowRight,
  Bell,
  CheckCircle,
  ClockClockwise,
} from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";

import { NotificationModel } from "services/notification.service";
import { useNotificationCenter } from "hooks/useNotificationCenter";
import { useNotificationSocket } from "hooks/useNotificationSocket";
import {
  getNotificationNavigationTarget,
  NotificationNavigationState,
} from "utils/notification";

const PAGE_SIZE = 20;

const Notification = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();

  const pageState =
    (location.state as NotificationNavigationState | null) ?? null;
  const selectedNotificationId = pageState?.selectedNotificationId ?? "";
  const {
    notificationList,
    unreadCount,
    hasMoreNotifications,
    isFetchingNotifications,
    isFetchingUnreadCount,
    isMarkingRead,
    isMarkingAllAsRead,
    refreshNotifications,
    scheduleRefreshNotifications,
    markNotificationAsReadById,
    markAllNotificationsAsRead,
    loadMoreNotifications,
  } = useNotificationCenter({
    pageSize: PAGE_SIZE,
  });

  useNotificationSocket(
    useCallback(() => {
      scheduleRefreshNotifications();
    }, [scheduleRefreshNotifications]),
  );

  const handleMarkAsRead = async (
    event: React.MouseEvent<HTMLButtonElement>,
    item: NotificationModel,
  ): Promise<void> => {
    event.stopPropagation();

    if (item.status === "read") {
      return;
    }

    try {
      await markNotificationAsReadById(item.id);
    } catch (error) {
      console.error("Mark notification as read failed:", error);
    }
  };

  const handleOpenRelated = async (item: NotificationModel): Promise<void> => {
    try {
      if (item.status === "unread") {
        await markNotificationAsReadById(item.id);
      }
    } catch (error) {
      console.error("Mark notification as read failed:", error);
    }

    const target = getNotificationNavigationTarget(item);

    navigate(target.pathname, {
      state: target.state,
    });
  };

  const handleLoadMore = async (): Promise<void> => {
    await loadMoreNotifications();
  };

  const handleMarkAllAsRead = async (): Promise<void> => {
    try {
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error("Mark all notifications as read failed:", error);
    }
  };

  return (
    <div className="p-6">
      <div className="rounded-3xl border border-[#E9EEF5] bg-white shadow-sm overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-[#EEF2F7] px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EFF6FF]">
              <Bell size={22} weight="duotone" className="text-[#2563EB]" />
            </div>

            <div>
              <h1 className="text-[24px] font-semibold text-[#0F172A]">
                Thông báo
              </h1>
              <p className="mt-1 text-[14px] text-[#64748B]">
                Bạn có {isFetchingUnreadCount ? "..." : unreadCount} thông báo
                chưa đọc
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleMarkAllAsRead()}
              disabled={isMarkingAllAsRead || unreadCount === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-[#BFDBFE] bg-white px-4 py-2 text-[14px] font-medium text-[#1D4ED8] hover:bg-[#EFF6FF] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isMarkingAllAsRead ? "Đang xử lý..." : "Đánh dấu tất cả đã đọc"}
            </button>

            <button
              type="button"
              onClick={() => void refreshNotifications()}
              className="inline-flex items-center gap-2 rounded-xl border border-[#D9E2EC] bg-white px-4 py-2 text-[14px] font-medium text-[#334155] hover:bg-[#F8FAFC]"
            >
              <ClockClockwise size={18} />
              Làm mới
            </button>
          </div>
        </div>

        {isFetchingNotifications && notificationList.length === 0 ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <Spin size="large" />
          </div>
        ) : notificationList.length === 0 ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <Empty description="Chưa có thông báo" />
          </div>
        ) : (
          <>
            <div className="divide-y divide-[#EEF2F7]">
              {notificationList.map((item) => {
                const isUnread = item.status === "unread";
                const isSelected = selectedNotificationId === item.id;

                return (
                  <div
                    key={item.id}
                    onClick={() => void handleOpenRelated(item)}
                    className={[
                      "cursor-pointer px-6 py-5 transition-all",
                      isUnread ? "bg-[#F8FBFF]" : "bg-white",
                      isSelected ? "ring-2 ring-inset ring-[#93C5FD]" : "",
                      "hover:bg-[#F8FAFC]",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={[
                          "mt-1 h-3 w-3 flex-shrink-0 rounded-full",
                          isUnread ? "bg-[#2563EB]" : "bg-[#CBD5E1]",
                        ].join(" ")}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-[16px] font-semibold text-[#0F172A]">
                                {item.title}
                              </h3>

                              {isUnread ? (
                                <span className="rounded-full bg-[#DBEAFE] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#1D4ED8]">
                                  Unread
                                </span>
                              ) : (
                                <span className="rounded-full bg-[#F1F5F9] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                                  Read
                                </span>
                              )}
                            </div>

                            <p className="mt-2 whitespace-pre-line text-[14px] leading-6 text-[#475569]">
                              {item.body}
                            </p>

                            <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-[#94A3B8]">
                              <span>
                                {new Date(item.createdAt).toLocaleString(
                                  "vi-VN",
                                )}
                              </span>

                              {item.type ? (
                                <span className="rounded-full bg-[#F8FAFC] px-2 py-1 text-[#64748B]">
                                  {item.type}
                                </span>
                              ) : null}

                              {item.orderId ? (
                                <span className="rounded-full bg-[#F8FAFC] px-2 py-1 text-[#64748B]">
                                  Order
                                </span>
                              ) : null}

                              {item.containerId ? (
                                <span className="rounded-full bg-[#F8FAFC] px-2 py-1 text-[#64748B]">
                                  Container
                                </span>
                              ) : null}

                              {item.routeId ? (
                                <span className="rounded-full bg-[#F8FAFC] px-2 py-1 text-[#64748B]">
                                  Route
                                </span>
                              ) : null}

                              {item.transportCostId ? (
                                <span className="rounded-full bg-[#F8FAFC] px-2 py-1 text-[#64748B]">
                                  Cost
                                </span>
                              ) : null}
                            </div>
                          </div>

                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            {isUnread ? (
                              <button
                                type="button"
                                onClick={(event) =>
                                  void handleMarkAsRead(event, item)
                                }
                                disabled={isMarkingRead}
                                className="inline-flex items-center gap-2 rounded-xl border border-[#BFDBFE] bg-white px-3 py-2 text-[13px] font-medium text-[#1D4ED8] hover:bg-[#EFF6FF] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <CheckCircle size={16} />
                                Đánh dấu đã đọc
                              </button>
                            ) : null}

                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleOpenRelated(item);
                              }}
                              className="inline-flex items-center gap-2 rounded-xl bg-[#0F172A] px-3 py-2 text-[13px] font-medium text-white hover:bg-[#1E293B]"
                            >
                              Xem chi tiết
                              <ArrowRight size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMoreNotifications ? (
              <div className="border-t border-[#EEF2F7] bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={() => void handleLoadMore()}
                  disabled={isFetchingNotifications}
                  className="w-full rounded-xl border border-[#D9E2EC] bg-white px-4 py-3 text-[14px] font-medium text-[#334155] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isFetchingNotifications ? (
                    <span className="inline-flex items-center gap-2">
                      <Spin size="small" />
                      Đang tải...
                    </span>
                  ) : (
                    "Xem thêm thông báo"
                  )}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default Notification;
